// src/services/mirrorOut.js — leaving a copy for my own phone.
//
// 🟥 THE MAILBOX ONLY EVER WORKED ONE WAY, and this is the missing half. The
//    phone WRITES sealed copies for its other devices (deviceMailbox.leaveCopies)
//    and this browser READS its own mailbox (deviceInbox.js) — so app→web was
//    covered and web→app had nothing at all. Send from here with the phone off,
//    let the recipient sweep the documents (they do, once their account is down
//    to one device), and those messages could never reach the handset. Not late.
//    Never.
//
// 🟢 AND THE LIVE PATH IS NOT THIS PATH. A phone that is ON gets the message
//    instantly over the socket (the server's self-fanout), and a phone that was
//    merely asleep reads the document when it next opens the chat. This covers
//    only the case where BOTH of those have failed — which is precisely why it
//    went unnoticed, and precisely why it loses data silently when it bites.

import {
  doc, getDoc, serverTimestamp, writeBatch,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { getIdentity } from '../crypto/identity';
import { mailboxAudience, mailboxPath } from './syncFanout';

const BATCH_MAX = 400;
/* one mirror per message per session — a re-render must not re-write the mailbox */
const _done = new Set();

/* ═══ THE BRAKES, AND WHY THEY EXIST ════════════════════════════════════════
   🟥 THIS FLOODED FIRESTORE. The caller hands over EVERY message of mine in the
      loaded window on every live update, and this then did, in one call: one
      getDoc per message ALL AT ONCE, then one mailbox write per message per owed
      device. A chat with 200 of my messages and two other devices is 200 parallel
      reads and 400 writes — per chat, on open. The browser reported it exactly:

        FirebaseError: [code=resource-exhausted]:
          Write stream exhausted maximum allowed queued writes.
        Using maximum backoff delay to prevent overloading the backend.

   🟥 AND IT COULD NOT RECOVER, BECAUSE THE GUARD WAS ONLY SET ON SUCCESS. `_done`
      was filled after the commit, so once commits started failing NOTHING was
      marked, and the next snapshot retried the whole history again — a failure
      that feeds itself. The failed-precondition errors on the receipt
      transactions in the same log are the collateral: their documents kept
      changing under them while the queue drained.

   🟢 THE MAILBOX IS A CATCH-UP, NOT AN ARCHIVE. A message I sent last week does
      not need one: if the phone was off that long, the backfill is what recovers
      it, and the mailbox TTL would have expired the copy anyway. Bounding this to
      recent messages costs nothing real and removes the burst entirely. */

/** Most messages one call may mirror. Newest first — the rest are the backfill's job. */
export const MIRROR_MAX = 20;
/** Older than this and a mailbox copy is pointless; the backfill covers it. */
export const MIRROR_MAX_AGE_MS = 24 * 60 * 60 * 1000;
/** After a failure, leave the account alone for this long rather than retrying instantly. */
export const MIRROR_COOLDOWN_MS = 60 * 1000;

let _blockedUntil = 0;

/**
 * Which of these ids this call should actually mirror.
 *
 * 🟢 A PURE RULE, so the brakes can be tested without a Firestore. Everything the
 *    decision needs is an argument.
 */
export function mirrorPlan(ids, opts = {}) {
  const { done = _done, chatId = '', now = Date.now(), blockedUntil = _blockedUntil } = opts;
  if (now < blockedUntil) return [];
  const seen = new Set();
  const out = [];
  for (const raw of ids || []) {
    const id = raw && String(raw);
    if (!id || id.startsWith('temp-')) continue;
    if (seen.has(id)) continue;
    seen.add(id);
    if (done.has(`${chatId}:${id}`)) continue;
    out.push(id);
  }
  /* 🟥 NEWEST FIRST, THEN CAPPED. Taking the first N of a window that arrives
     oldest-first would mirror the least useful messages and never reach the ones
     that actually need catching up. */
  return out.slice(-MIRROR_MAX);
}

/* the device list changes on the order of days; a burst of sends reads it once */
const DEVICES_TTL_MS = 60 * 1000;
let _owedCache = null;   // { uid, at, owed }

/**
 * Leave every other device on my account a copy of a message I am sending NOW.
 *
 * 🟥 THE ROW IS THE ONE BEING WRITTEN, NOT ONE READ BACK. mirrorOut below re-reads
 *    the document from the cloud — and by then the recipient's phone has often
 *    swept it (it deletes a delivered message within a second), so the copy was
 *    never made and my phone never got the message. Here there is nothing to read
 *    and nothing to lose the race to. `timestamp: serverTimestamp()` inside the row
 *    is a sentinel Firestore resolves on write, nested or not.
 */
export async function mirrorSentRow(uid, chatId, messageId, row) {
  if (!uid || !chatId || !messageId || !row) return 0;
  const key = `${chatId}:${messageId}`;
  if (_done.has(key)) return 0;
  const me = await getIdentity().catch(() => null);
  let owed = [];
  try {
    const now = Date.now();
    if (_owedCache && _owedCache.uid === uid && now - _owedCache.at < DEVICES_TTL_MS) {
      owed = _owedCache.owed;
    } else {
      const snap = await getDoc(doc(db, 'users', uid));
      owed = mailboxAudience(snap.data()?.devices, { myDeviceId: me?.deviceId, now });
      _owedCache = { uid, at: now, owed };
    }
  } catch {
    return 0;   // cannot tell who is owed → the ChatWindow catch-up still runs
  }
  if (!owed.length) return 0;
  const batch = writeBatch(db);
  for (const d of owed) {
    const p = mailboxPath(uid, d.deviceId);
    batch.set(doc(db, p[0], p[1], p[2], p[3], p[4], `${chatId}__${messageId}`), {
      chatId, messageId, at: serverTimestamp(), row,
    });
  }
  await batch.commit();
  _done.add(key);
  return owed.length;
}

/** Tests only — module state outlives a render. */
export function _resetMirror() { _done.clear(); _blockedUntil = 0; _owedCache = null; }

/**
 * Copy messages I just sent into the mailbox of every other device on my account.
 *
 * 🟥 THE DOCUMENT IS COPIED VERBATIM, sealed fields and all. This browser cannot
 *    re-seal for a device whose key it does not hold, and it does not need to:
 *    the sender already sealed a copy for every device of both parties, so the
 *    phone's own copy is sitting inside the row we are handing it.
 */
export async function mirrorOut(uid, chatId, messageIds) {
  if (!uid || !chatId || !messageIds?.length) return 0;
  const me = await getIdentity().catch(() => null);
  const ids = mirrorPlan(messageIds, { chatId });
  if (!ids.length) return 0;

  let owed = [];
  try {
    const snap = await getDoc(doc(db, 'users', uid));
    /* 🟥 THIS ASKED owedDevices, WHICH IS THE PHONE'S QUESTION AND ANSWERS
       "which linked BROWSERS must be left a copy". A phone is not a browser, so
       it came back EMPTY on every call — the web has never written a single row
       into the phone's mailbox. runMailboxImport therefore always drained an
       empty collection, and the skeletons, the progress line and the "importing
       from the web" row in ChatsTab have never had anything to react to.
       🟢 mailboxAudience is the same freshness/key/self rule without the browser
          filter: every OTHER device on my account, phones included. */
    owed = mailboxAudience(snap.data()?.devices, { myDeviceId: me?.deviceId });
  } catch {
    return 0;   // 🟥 cannot tell who is owed → write nothing rather than guess
  }
  if (!owed.length) return 0;

  const rows = await Promise.all(ids.map(async (id) => {
    try {
      const s = await getDoc(doc(db, 'chats', chatId, 'messages', id));
      return s.exists() ? { id, data: s.data() } : null;
    } catch { return null; }
  }));
  const live = rows.filter(Boolean);
  if (!live.length) return 0;

  let batch = writeBatch(db);
  let n = 0;
  let written = 0;
  const flush = async () => { if (n) { await batch.commit(); batch = writeBatch(db); n = 0; } };
  try {
    for (const d of owed) {
      const p = mailboxPath(uid, d.deviceId);
      for (const r of live) {
        /* 🟢 THE CHAT ID TRAVELS WITH THE ROW — a message document does not carry
           the chat it belongs to, the path does, and a mailbox is flat. Same
           shape the phone writes, so one drain reads both. */
        batch.set(doc(db, p[0], p[1], p[2], p[3], p[4], `${chatId}__${r.id}`), {
          chatId, messageId: r.id, at: serverTimestamp(), row: r.data,
        });
        n += 1;
        written += 1;
        if (n >= BATCH_MAX) await flush();   // eslint-disable-line no-await-in-loop
      }
    }
    await flush();
    for (const r of live) _done.add(`${chatId}:${r.id}`);
  } catch {
    /* 🟥 MARK THEM TRIED, AND STAND DOWN. Returning without recording anything is
       what turned one failed commit into a permanent retry storm: the next
       snapshot asked for the same hundreds of writes, which kept the queue full,
       which failed again. A mailbox that will not write is a phone that catches
       up the ordinary way — this is the last resort, not the first. */
    _blockedUntil = Date.now() + MIRROR_COOLDOWN_MS;
    for (const r of live) _done.add(`${chatId}:${r.id}`);
    return 0;
  }
  return written;
}
